import { ref, listAll, getMetadata } from "firebase/storage";
import { storage } from "../firebase.js";
import { getCachedDownloadUrl } from "./firebaseStorageUrl.js";

const FILE_EXTENSIONS_BY_TYPE = {
  images: ["jpg", "jpeg", "png", "gif", "bmp", "webp"],
  pdf: ["pdf"],
  audio: ["mp3", "wav", "ogg", "m4a"],
};

const listResolvedCache = new Map();
const listInFlightCache = new Map();

function getListCacheKey(uid, fileType) {
  return `${uid}:${fileType || "all"}`;
}

function getValidExtensions(fileType) {
  return FILE_EXTENSIONS_BY_TYPE[fileType] || [""];
}

function dedupeFilesByName(files) {
  const unique = [];
  const seen = new Set();
  for (const file of files) {
    if (file && !seen.has(file.name)) {
      unique.push(file);
      seen.add(file.name);
    }
  }
  return unique;
}

/**
 * Fetch a user's Storage file list with session caching and in-flight deduplication.
 * @returns {Promise<{ files: Array<{ name, path, timeCreated, size }>, totalBytes: number }>}
 */
export async function fetchUserFileList({ uid, fileType, forceRefresh = false }) {
  if (!uid) {
    throw new Error("uid is required");
  }

  const cacheKey = getListCacheKey(uid, fileType);

  if (!forceRefresh && listResolvedCache.has(cacheKey)) {
    return listResolvedCache.get(cacheKey);
  }

  if (!forceRefresh && listInFlightCache.has(cacheKey)) {
    return listInFlightCache.get(cacheKey);
  }

  const promise = (async () => {
    const validExtensions = getValidExtensions(fileType);
    const listRef = ref(storage, `users/${uid}`);
    const res = await listAll(listRef);

    const allMetas = await Promise.all(res.items.map((item) => getMetadata(item).catch(() => null)));
    const totalBytes = allMetas.reduce((sum, metadata) => sum + (metadata?.size || 0), 0);

    const next = [];
    await Promise.all(
      res.items.map(async (itemRef) => {
        const ext = itemRef.name.split(".").pop().toLowerCase();
        if (!validExtensions.includes(ext)) return;

        const objectPath = itemRef.fullPath || `users/${uid}/${itemRef.name}`;
        const [url, metadata] = await Promise.all([
          getCachedDownloadUrl(objectPath),
          getMetadata(itemRef).catch(() => null),
        ]);

        next.push({
          name: itemRef.name,
          path: url,
          timeCreated: metadata?.timeCreated || new Date().toISOString(),
          size: metadata?.size || 0,
        });
      })
    );

    const result = {
      files: dedupeFilesByName(next),
      totalBytes,
    };
    listResolvedCache.set(cacheKey, result);
    return result;
  })();

  listInFlightCache.set(cacheKey, promise);

  try {
    return await promise;
  } finally {
    listInFlightCache.delete(cacheKey);
  }
}

export function invalidateUserFileListCache(uid) {
  if (!uid) return;
  const prefix = `${uid}:`;
  for (const key of [...listResolvedCache.keys()]) {
    if (key.startsWith(prefix)) {
      listResolvedCache.delete(key);
    }
  }
  for (const key of [...listInFlightCache.keys()]) {
    if (key.startsWith(prefix)) {
      listInFlightCache.delete(key);
    }
  }
}

/** @internal tests only */
export function clearFileListCache() {
  listResolvedCache.clear();
  listInFlightCache.clear();
}
